import type { FAQ } from '../../../../app/package/base/cms/package/cms-domain';
import type { RobotsContent } from '../../../../app/package/base/types';

export function updateMetaTitle(title?: string) {
  const newTitle = title ?? window.$app.appConfig.metaTitle;
  if (document.title !== newTitle) {
    document.title = newTitle;
  }
}

export function updateMetaDescription(description?: string) {
  const metaDescription = document.querySelector('meta[name="description"]');
  const newContent = description ?? window.$app.appConfig.metaDescription;
  // Only update the attribute if it's different
  if (metaDescription && metaDescription.getAttribute('content') !== newContent) {
    metaDescription.setAttribute('content', newContent);
  }
}

let nextPreloadLinkId = 0;
const LD_JSON_FAQ = 'ldJSONFAQ';

export function addMetaPreloadLink(
  href: string,
  as: 'fetch' | 'font' | 'image' | 'script' | 'style' | 'track' = 'image',
  asFirstElement = false,
  anonymous: boolean | undefined = undefined,
  type: string | undefined = undefined,
): void {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = href;
  link.fetchPriority = 'high';
  if (anonymous !== undefined) {
    link.crossOrigin = anonymous ? 'anonymous' : 'use-credentials';
  }
  if (type) {
    // Example: type="video/mp4"
    link.type = type;
  }
  const id = `preload-link-${nextPreloadLinkId++}`;
  link.id = id;
  if (asFirstElement) {
    document.head.insertBefore(link, document.head.firstChild);
  } else {
    document.head.appendChild(link);
  }
  window.$app.logger.log(`preload [${as}]: ${href}`);

  if (window.$app.appConfig.prerenderMode) {
    return;
  }

  // Schedule removal of the link after 15 seconds
  setTimeout(() => {
    const el = document.getElementById(id);
    if (el) {
      el.remove();
      window.$app.logger.log(`preload removed: ${id}`);
    }
  }, 15_000);
}

/**
 * Add prefetch meta tag to the head of the document.
 */
export function addMetaPrefetchLink(href: string, as = 'document', crossOrigin = true): void {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  link.as = as;
  if (crossOrigin) {
    link.crossOrigin = 'anonymous';
  }
  window.$app.logger.log(`Add meta prefetch link: '${href}'`);
  document.head.appendChild(link);
}

/**
 * Add `speculationrules˚ script tag to the head of the document.
 * - prefetch: Download the response body of the referenced pages, but none of the subresources referenced by the page.
 * - prerender: Fetch, render, and load the content into an invisible tab. This includes loading all subresources, running all JavaScript, and even loading subresources and performing data fetches started by JavaScript.
 *
 * Third-party pages need to opt-in using "supports-loading-mode: credentialed-prerender" in the header.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Speculation_Rules_API
 *
 * Used by Sports for login as follows:
 * <script type="speculationrules" id="loginSpeculationPrerender">
 *  {
 *    "prerender": [
 *      {
 *        "urls": [
 *          "https://account.tipico.de/v1/tpapi/cafes/auth/?referrer=https%3A%2F%2Fsports.tipico.com%2F&selectedOdds=&encryptedBetId=&origin=app-tipico-sports&client_type=DESKTOP_WEB_RETH&client_id=app-tipico-sports&redirect_uri=https%3A%2F%2Fsports.tipico.de%2Fv1%2Ftpapi%2Ftaas%2Fsession%2Fapp-tipico-sports&response_type=code"
 *        ]
 *      }
 *    ]
 *  }
 */
export type SpeculationRulesUrls = string[] | undefined;
export function addSpeculationRules(preFetchUrls: SpeculationRulesUrls, preRenderUrls: SpeculationRulesUrls): void {
  try {
    const rulesTag = document.createElement('script');
    rulesTag.type = 'speculationrules';
    const rules: any = {};
    if (preFetchUrls) {
      rules.prefetch = [{ urls: preFetchUrls }];
    }
    if (preRenderUrls) {
      rules.prerender = [{ urls: preRenderUrls }];
    }
    window.$app.logger.log('Add Speculation Rules:', rules);
    rulesTag.textContent = JSON.stringify(rules);
    document.head.appendChild(rulesTag);
  } catch (err) {
    window.$app.logger.error('Failed to add Speculation Rules:', err);
  }
}

function getRobotsTag(): HTMLMetaElement | null {
  const el = document.querySelector('meta[name="robots"]');
  if (!el) {
    window.$app.logger.warn('No existing <meta name="robots"> to adjust.');
    return null;
  }
  return el as HTMLMetaElement;
}

/**
 * Update the content of the robots meta tag.
 * https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag#directives
 */
export function updateRobotsTag(content: RobotsContent) {
  const el = getRobotsTag();
  if (el === null) {
    return;
  }
  if (el.getAttribute('content') === 'none' && (content === 'noindex' || content === 'nofollow')) {
    // Keep 'none' if the new setting is less restrictive
    // This is important as staging environments may have 'none' set.
    window.$app.logger.log('Keep current robots as existing setting is more restrictive.');
    return;
  }
  el.setAttribute('content', content);
}

export function appendOpenGraph(tag: string, content: string, dataSet: string) {
  const meta = document.createElement('meta');
  meta.setAttribute('property', `og:${tag}`);
  meta.setAttribute('content', content);
  meta.setAttribute(`data-${dataSet}`, '');
  document.head.appendChild(meta);
}

export function unloadOpenGraph(dataset: string) {
  const ogMetaTags = document.querySelectorAll(`meta[data-${dataset}]`);
  ogMetaTags.forEach((tag) => {
    document.head.removeChild(tag);
  });
}

export function addLdJsonFAQ(faq: FAQ[]) {
  removeLdJsonFAQ();

  const ldJsonScript = document.createElement('script');
  ldJsonScript.type = 'application/ld+json';
  ldJsonScript.id = LD_JSON_FAQ;
  ldJsonScript.innerHTML = `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [${faq
      .map(
        (item) => `
          {
            "@type": "Question",
            "name": ${JSON.stringify(item.title)},
            "acceptedAnswer": {
              "@type": "Answer",
              "text": ${JSON.stringify(removeMarkup(item.content))}
            }
          }
        `,
      )
      .join(',')}
    ]
  }`;

  document.body.appendChild(ldJsonScript);
}

export function removeLdJsonFAQ() {
  const ldJsonScript = document.getElementById(LD_JSON_FAQ);
  if (ldJsonScript) {
    ldJsonScript.remove();
  }
}

export function cleanupRichText(html: string, exceptionTag?: string): string {
  const exceptionPattern = exceptionTag ? `(?!${exceptionTag}\\b)` : '';
  return html
    .replace(/<style[^>]*>.*?<\/style>/gs, '') // remove inline styles
    .replace(/<script[^>]*>.*?<\/script>/gs, '') // remove inline scripts
    .replace(/ style="[^"]*"/g, '') // remove style attribute
    .replace(new RegExp(`<(${exceptionPattern}\\w+)[^>]*>\\s*&nbsp;\\s*</\\1>`, 'g'), '') // Remove tags with only &nbsp; except exceptionTag
    .replace(new RegExp(`<(${exceptionPattern}\\w+)[^>]*>\\s*<br\\s*\\/?>\\s*</\\1>`, 'g'), ' ') // Remove tags with only <br> or <br/> except exceptionTag
    .replace(new RegExp(`<(${exceptionPattern}\\w+)[^>]*></\\1>`, 'g'), '') // Remove empty tags except exceptionTag
    .replace(new RegExp(`<(${exceptionPattern}\\w+)[^>]*>\\s*</\\1>`, 'g'), '') // Remove tags with only whitespace except exceptionTag
    .replace(/<!\s*--.*?-->/g, '') // Remove comments
    .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>') // Remove multiple consecutive <br> tags
    .replace(/\s+/g, ' ') // Remove extra spaces
    .trim();
}

export function removeMarkup(html: string): string {
  return cleanupRichText(html)
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/<\/?span[^>]*>/gi, '')
    .replace(/<\/?ul[^>]*>/gi, '')
    .replace(/<li[^>]*>/gi, '')
    .replace(/<\/li[^>]*>/gi, ' ');
}

export function resetRobotsTagToDefault() {
  const el = getRobotsTag();
  if (el === null) {
    return;
  }
  el.setAttribute('content', window.$app.appConfig.metaRobotsDefault);
}

/**
 * Creates a canonical URL based on the current window location and the canonical origin defined in the app configuration.
 */
export function createCanonicalUrl(): string {
  const currentUrl = new URL(window.location.href);
  return `${window.$app.appConfig.canonicalOrigin}${currentUrl.pathname}`;
}

/**
 * Adds a canonical URL to the head of the document.
 * If a canonical URL already exists, it will be updated.
 */
export function updateCanonicalUrl(targetCanonicalUrl?: string): void {
  const currentUrl = new URL(window.location.href);
  const canonicalUrl = targetCanonicalUrl ?? `${window.$app.appConfig.canonicalOrigin}${currentUrl.pathname}`;
  const canonicalLinkElement = document.querySelector('link[rel="canonical"]');

  if (canonicalLinkElement) {
    const existingCanonicalUrl = canonicalLinkElement.getAttribute('href');
    if (existingCanonicalUrl !== canonicalUrl) {
      canonicalLinkElement.setAttribute('href', canonicalUrl);
      window.$app.logger.log('Added canonical URL:', canonicalUrl);
    }
  } else {
    const newCanonicalLinkElement = document.createElement('link');
    newCanonicalLinkElement.rel = 'canonical';
    newCanonicalLinkElement.href = canonicalUrl;
    document.head.appendChild(newCanonicalLinkElement);
    window.$app.logger.log('Added canonical URL:', canonicalUrl);
  }
}

/**
 * Adds a meta tag with passed attributes if it already does not exist.
 * Example: addMetaTag({ name: 'apple-itunes-app', content: 'app-id=12345' }) => <meta name="apple-itunes-app" content="app-id=12345">
 */
export function addMetaTag(attributes: { [key: string]: string | undefined }): void {
  const selector = Object.entries(attributes)
    .map(([key, value]) => (value !== undefined ? `[${key}="${value}"]` : ''))
    .join('');

  const existingMetaTag = document.head.querySelector(`meta${selector}`);

  if (!existingMetaTag) {
    const metaTag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== undefined) {
        metaTag.setAttribute(key, value);
      }
    });
    document.head.appendChild(metaTag);
  }
}
